import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, ArrowLeft, LogIn } from 'lucide-react';
import { loginTeacher } from '../../services/authService';
import AuthLayout from '../ui/AuthLayout';
import GlassCard from '../ui/GlassCard';
import Input from '../ui/Input';
import Button from '../ui/Button';

/**
 * Teacher Login Screen
 * Email + Password
 */
const TeacherLogin = ({ onSuccess, onNavigate }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [rememberMe, setRememberMe] = useState(true);
  const [logging, setLogging] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLogging(true);

    try {
      // Attempt login
      const result = await loginTeacher(
        formData.email.toLowerCase(),
        formData.password
      );

      if (result.success) {
        if (onSuccess) onSuccess(result.teacher, result.session);
      } else {
        setError(result.error || 'Login failed');
        setFormData(prev => ({ ...prev, password: '' }));
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLogging(false);
    }
  };

  return (
    <AuthLayout
      title="Teacher Portal"
      subtitle="Manage your classroom with AI"
    >
      <div className="max-w-md mx-auto">
        <GlassCard>
          {/* Back Button */}
          <button
            onClick={() => onNavigate && onNavigate('landing')}
            className="absolute top-6 left-6 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <form onSubmit={handleSubmit} className="space-y-5 mt-4">
            <Input
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="rajesh.kumar@stmarysschool.edu"
              icon={Mail}
              required
            />

            <Input
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••"
              icon={Lock}
              required
            />

            {/* Remember Me & Error */}
            <div className="flex items-center justify-between">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span className="ml-2 text-sm text-gray-600">Remember me</span>
              </label>
              <span className="text-sm text-purple-600 hover:text-purple-700 cursor-pointer">
                Forgot password?
              </span>
            </div>

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="text-red-500 text-sm font-medium text-center"
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <Button
              type="submit"
              variant="primary"
              loading={logging}
              className="w-full"
              icon={LogIn}
            >
              Login to Dashboard
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-gray-500 space-y-2">
            <p>
              Student?{' '}
              <button
                type="button"
                onClick={() => onNavigate && onNavigate('student-login')}
                className="text-purple-600 hover:text-purple-700 font-medium"
              >
                Login here
              </button>
            </p>
            <p>
              New institution?{' '}
              <button
                type="button"
                onClick={() => onNavigate && onNavigate('institution-register')}
                className="text-purple-600 hover:text-purple-700 font-medium"
              >
                Register here
              </button>
            </p>
          </div>
        </GlassCard>

        {/* Dev Warning */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-6 p-4 bg-yellow-50/80 backdrop-blur-sm border border-yellow-200/50 rounded-xl text-center">
            <p className="text-xs text-yellow-800 font-medium">
              🔧 DEV MODE
            </p>
            <p className="text-xs text-yellow-700 mt-1">
              Email: aman@fluence.ac | Pass: aman@123
            </p>
          </div>
        )}
      </div>
    </AuthLayout>
  );
};

export default TeacherLogin;
